import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { isAuthenticated } from "../../Auth/auth";
import { Error, Success } from "../../Messages/Messages";
import { FaArrowLeftLong } from "react-icons/fa6";
import { Button, Tabs, Progress, Flex, Tag } from "antd";
import { MilestoneDocument } from "./MilestoneDocuments/MilestoneDocuments";
import { TiTickOutline } from "react-icons/ti";
import AddInvoices from "../../Billing/Invoices/AddInvoices";
import { TasksComponent } from "../../Tasks/Tasks";
import IndividualMilestoneInvoices from "../ProjectDetailsView/Invoices/IndividualMilestoneInvoices";
import GeneralTable from "../../GeneralTable";
import { SocketContext } from "../../../context/SocketContext";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import moment from "moment";
import TaskDrawer from "../../../serviceProvider/components/tasks/TaskDrawer";

import { useTranslation } from "react-i18next";
import "./MilestoneDetails.css";

export const MilestoneDetails = ({ milestone, goBack, updateParent }) => {
  const { t } = useTranslation("dashboard");
  const socket = useContext(SocketContext);
  const [milestoneObj, setMilestoneObj] = useState({});
  const [milestoneEmployees, setMilestoneEmployees] = useState([]);
  const [simpleTasksArray, setSimpleTasksArray] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const getTasks = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/tasks/filters`,
        { milestone: milestone?._id, serviceProvider: isAuthenticated()?.serviceProviderId },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      if (res.status === 200 || res.status === 304) {
        const tasks = res.data;
        setSimpleTasksArray(tasks);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    getTasks();
    return () => {};
  }, []);

  useEffect(() => {
    setMilestoneObj(milestone);

    const makeOptions = milestone?.team?.map(d => ({
      label: `${d?.firstName} ${d?.lastName}`,
      value: d?._id
    }));
    setMilestoneEmployees(makeOptions);

    return () => {};
  }, [milestone]);

  const onTaskReArrange = async message => {
    if (message) {
      await socket.emit("sendProjectUpdate", {
        milestone: milestone?._id,
        message
      });
    }
  };

  const markTaskAsComplete = async () => {
    await axios
      .put(
        `${process.env.REACT_APP_BACKEND_URL}/milestones/status/update/${milestoneObj?._id}`,
        { status: "COMPLETED" },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      )
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          Success(res.data.successMessage);

          setShowInvoiceModal(true);
          setMilestoneObj(prev => ({ ...prev, status: "COMPLETED" }));
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        if (err.response?.data?.errorMessage) {
          Error(err.response?.data?.errorMessage);
        } else {
          Error(err.message);
        }
      });
  };

  const columns = [
    {
      title: t("Item Name"),
      dataIndex: "itemName",
      mobile: "title",
      sorter: (a, b) => a.itemName.localeCompare(b.itemName),
      render: (text, record) => record?.item?.itemName
    },
    {
      title: t("Total"),
      dataIndex: "total",
      sorter: (a, b) => a.total - b.total
    },
    {
      title: t("Used"),
      dataIndex: "used",
      render: (text, record) => record.used || 0,
      sorter: (a, b) => (a.used || 0) - (b.used || 0)
    },
    {
      title: t("Available"),
      key: "available",
      render: (text, record) => record.total - (record.used || 0),
      sorter: (a, b) => a.total - (a.used || 0) - (b.total - (b.used || 0))
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "subtitle",
      render: (text, record) => {
        const available = record.total - (record.used || 0);
        let status = "IN STOCK";
        if (available <= 0) {
          status = "OUT OF STOCK";
        } else if (record.minimumAmount && available <= record.minimumAmount) {
          status = "LOW STOCK";
        }
        return <Tag color={status !== "IN STOCK" ? "error" : "success"}>{t(status)}</Tag>;
      }
    },
    {
      title: t("Last Updated"),
      dataIndex: "updatedAt",
      render: text => moment(text).format("DD/MM/YYYY")
    }
  ];

  const tabItems = [
    {
      key: "1",
      label: t("Inventory"),
      children: <GeneralTable data={milestoneObj?.inventory} columns={columns} />
    },
    {
      key: "2",
      label: t("Tasks"),
      children: (
        <>
          <div className="milestone-details__tasks-bar">
            <TaskDrawer
              mode="add"
              updateParent={getTasks}
              extra={{
                project: milestone?.projectId?._id || milestone?.projectId,
                milestone: milestone?._id
              }}
            />
          </div>
          <TasksComponent
            onTaskReArrange={onTaskReArrange}
            tasks={simpleTasksArray}
            updateData={getTasks}
          />
        </>
      )
    },
    {
      key: "4",
      label: t("Documents"),
      children: <MilestoneDocument milestone={milestone} />
    },
    {
      key: "5",
      label: t("Invoices"),
      children: <IndividualMilestoneInvoices milestone={milestone} />
    }
  ];

  return (
    <div className="milestone-details">
      <Flex
        className="milestone-details__header"
        justify="space-between"
        align="center"
        gap={12}
        wrap="wrap"
      >
        <Flex gap={8} align="center" className="milestone-details__heading">
          <Button
            type="text"
            className="milestone-details__back"
            icon={<FaArrowLeftLong />}
            aria-label={t("Back")}
            onClick={goBack}
          />
          <h2 className="milestone-details__title">{t("Milestone Details")}</h2>
        </Flex>
        {milestoneObj?.status === "COMPLETED" ? (
          <Progress type="circle" percent={100} size={32} />
        ) : (
          <Button
            color="green"
            variant="solid"
            icon={<TiTickOutline />}
            onClick={markTaskAsComplete}
          >
            {t("Mark As Completed")}
          </Button>
        )}
      </Flex>
      {showInvoiceModal && (
        <AddInvoices
          openModal={showInvoiceModal}
          tasks={simpleTasksArray}
          closeModal={() => setShowInvoiceModal(!showInvoiceModal)}
          updateParent={updateParent}
          milestone={milestone?._id}
          prefillData={milestone}
          project={milestone?.projectId}
        />
      )}
      <Tabs defaultActiveKey="1" items={tabItems} />
    </div>
  );
};

export default MilestoneDetails;
